import type { ComputedRef, Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type {
  InitialData as WeeklyReportSearchFormInitialData,
  ValidData as WeeklyReportSearchFormValidData,
} from '@/components/searchForms/WeeklyReportSearchForm.vue';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { format } from 'date-fns';
import _isEqual from 'lodash/isEqual';

import { useUserStore } from '@/stores/useUserStore';
import { fetchGetReportWeeklyStat } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import { ReportWeeklyStats } from '@/models/reports';

export interface WeeklyReportViewComposable {
  search: (formData: WeeklyReportSearchFormValidData) => void;
  initialData: ComputedRef<WeeklyReportSearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  statsReport: Ref<ReportWeeklyStats | null>;
}

export default function useWeeklyReportView(
  queryDates: Readonly<Ref<[Date, Date] | undefined>>,
  queryStates: Readonly<Ref<string[] | undefined>>,
): WeeklyReportViewComposable {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();

  const userTimeZone = computed(() => userStore.user.timezone);

  const initialData = computed<WeeklyReportSearchFormInitialData>(() => {
    let states: string[] | undefined;
    if (queryStates.value) states = queryStates.value.slice();
    return {
      dates: queryDates.value,
      states,
    };
  });

  const isFetchingData = ref(false);
  const statsReport = ref<ReportWeeklyStats | null>(null);

  function search(formData: WeeklyReportSearchFormValidData): void {
    const dates = [
      format(formData.dates[0], 'yyyy-MM-dd'),
      format(formData.dates[1], 'yyyy-MM-dd'),
    ].join(',');
    const query = { ...route.query, dates, states: formData.states.join(',') };
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.WEEKLY_STATS_REPORT,
      query,
      force: true,
      replace: isReplace,
    });
  }

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    const dates = queryDates.value;
    const states = queryStates.value;
    statsReport.value = null;
    if (!dates || !states) return;
    isFetchingData.value = true;
    await fetchGetReportWeeklyStat(userTimeZone.value, { dates, states })
      .then((data) => {
        if (!_isEqual(route.query, newQuery)) {
          return;
        }
        statsReport.value = data;
      })
      .finally(() => {
        isFetchingData.value = false;
      });
  }

  watch(
    route,
    async (newRoute) => {
      if (newRoute.name === routeNames.WEEKLY_STATS_REPORT) {
        await fetchData(newRoute.query);
      }
    },
    { immediate: true },
  );

  return {
    search,
    initialData,
    isFetchingData,
    statsReport,
  };
}
